import { Emoji } from '@emoji-mart/data';
import {
  InputBase,
  InputBaseComponentProps,
  Typography,
  alpha,
  styled,
} from '@mui/material';
import { Box } from '@mui/system';
import { Editor } from '@tiptap/core';
import React from 'react';
import { EmojiPopover } from 'src/components/EmojiPickerPopover';
import { useLocales } from 'src/locales';

type Props = {
  editor: Editor;
  InputComponent:
    | React.ElementType<
        InputBaseComponentProps,
        keyof React.JSX.IntrinsicElements
      >
    | undefined;
  onSend: () => void;
  cancelEdition: () => void;
};
export const ConversationEditInput = ({
  editor,
  InputComponent,
  onSend,
  cancelEdition,
}: Props) => {
  const { translate } = useLocales();
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) return onSend();
    if (e.key === 'Escape') return cancelEdition();
    return null;
  };

  const handleSelectEmoji = (item: Emoji) => {
    const rangeFrom = editor?.state.selection.from;
    editor.chain().addEmoji({ item, rangeFrom }).focus(rangeFrom).run();
  };

  return (
    <Box>
      <StyledInputBase
        inputComponent={InputComponent}
        inputProps={{ editor }}
        onKeyDown={handleKeyDown}
        endAdornment={
          <EmojiPopover
            onSelectEmoji={(emoji) => handleSelectEmoji(emoji)}
            arrow="right-center"
          />
        }
      />
      <Typography variant="caption">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <span>{translate('communicate.chat.esc_to')}</span>
          <Box
            onClick={cancelEdition}
            sx={(theme) => ({
              color: theme.palette.primary.main,
              cursor: 'pointer',
            })}
          >
            {translate('shared.buttons.cancel')}
          </Box>
          <span>•</span>
          <span>{translate('communicate.chat.enter_to')}</span>
          <Box
            onClick={onSend}
            sx={(theme) => ({
              color: theme.palette.primary.main,
              cursor: 'pointer',
            })}
          >
            {translate('shared.buttons.save')}
          </Box>
        </Box>
      </Typography>
    </Box>
  );
};

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  padding: theme.spacing(0.5, 1.5),
  borderRadius: theme.spacing(1),
  width: '100%',
  flexShrink: 0,
  backgroundColor: alpha(theme.palette.divider, 0.08),
  height: 'auto',
}));
